import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {AlertContainerStyled} from "../../../styled/Alert";
import { BiSolidErrorAlt } from "react-icons/bi";
import { GoCheckCircleFill } from "react-icons/go";
import { IoHelpCircle } from "react-icons/io5";
import { useTheme } from "../Theme/ThemeContext";

import { useDispatch } from 'react-redux';
import { setError, setSuccess, setInfo } from '../../../redux/states';
import { IoClose } from 'react-icons/io5';

const Alert = ({ error, info, success }) => {
    const { themeName } = useTheme();
    const [visible, setVisible] = useState(false);
    const [fadingOut, setFadingOut] = useState(false);
    const dispatch = useDispatch();

    const active = useMemo(() => {
        if (error?.state) {
            return { variant: "error", message: error.message, ts: error.ts };
        }
        if (success?.state) {
            return { variant: "success", message: success.message, ts: success.ts };
        }
        if (info?.state) {
            return { variant: "info", message: info.message, ts: info.ts };
        }
        return null;
    }, [error?.state, error?.message, error?.ts, success?.state, success?.message, success?.ts, info?.state, info?.message, info?.ts]);

    const lastVariantRef = useRef(null);
    const timerRef = useRef(null);

    const handleClose = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setFadingOut(true);
        // shorten fade out to feel responsive
        const variant = lastVariantRef.current || active?.variant;
        setTimeout(() => {
            setVisible(false);
            if (variant === 'error') dispatch(setError({ state: false, message: '', ts: null }));
            if (variant === 'success') dispatch(setSuccess({ state: false, message: '', ts: null }));
            if (variant === 'info') dispatch(setInfo({ state: false, message: '', ts: null }));
        }, 180);
    }, [active?.variant, dispatch]);

    useEffect(() => {
        if (active) {
            lastVariantRef.current = active.variant;
            setVisible(true);
            setFadingOut(false);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                handleClose();
            }, 5000);
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
            };
        }
    }, [active, handleClose]);

    if (!visible || !active) return null;

    const renderIcon = () => {
        switch (active.variant) {
            case "success": return <GoCheckCircleFill />;
            case "info": return <IoHelpCircle />;
            default: return <BiSolidErrorAlt />;
        }
    };

    return (
        <AlertContainerStyled
            variant={active.variant}
            theme={themeName}
            className={fadingOut && 'slide-out'}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
        >
            <div className="line slide-in"></div>

            <div className="alert BubblePopIn">
                <button
                    type="button"
                    className="closeBtn"
                    onClick={(e) => { e.stopPropagation(); handleClose(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') { e.preventDefault(); handleClose(); } }}
                    aria-label="Dismiss notification"
                >
                    <IoClose />
                </button>
                <div className="icon">{renderIcon()}</div>
                <p className="errorText">{active.message}</p>
            </div>
        </AlertContainerStyled>
    );
};

export default Alert;